import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeftRight, ShoppingBag, AlertTriangle, Info, AlertCircle, CheckCircle, Wrench, Bell } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';

// Mappa actionUrl del backend alle route del frontend (es. /wallet -> /deposit, /notifications -> profile con tab)
const normalizeNotificationActionUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const path = url.trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return { type: 'external', url: path };
  if (path === '/wallet') return { type: 'internal', path: '/deposit' };
  if (path === '/notifications') return { type: 'internal', path: '/profile', state: { openTab: 'notifications' } };
  if (path.startsWith('/')) return { type: 'internal', path };
  return null;
};

const NotificationsTab = ({ notifications, total, unreadCount, announcements, maintenanceAlerts, loading, loadingCommunications, onMarkAsRead, onMarkAllAsRead }) => {
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 3;
  const { t, locale } = useLanguage();
  const [maintenancePage, setMaintenancePage] = useState(0);
  const [announcementsPage, setAnnouncementsPage] = useState(0);
  const [notificationsPage, setNotificationsPage] = useState(0);
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateTime(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }, locale);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'prize': return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 'tournament': return <Trophy className="w-5 h-5 text-blue-500" />;
      case 'trade': return <ArrowLeftRight className="w-5 h-5 text-green-500" />;
      case 'marketplace': return <ShoppingBag className="w-5 h-5 text-purple-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'success': return <Info className="w-5 h-5 text-green-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getAnnouncementIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'maintenance': return <Wrench className="w-5 h-5 text-orange-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getAnnouncementBgColor = (type, priority) => {
    if (priority === 'critical') return 'border-red-500/50 bg-red-500/10';
    switch (type) {
      case 'warning': return 'border-yellow-500/50 bg-yellow-500/10';
      case 'error': return 'border-red-500/50 bg-red-500/10';
      case 'success': return 'border-green-500/50 bg-green-500/10';
      case 'maintenance': return 'border-orange-500/50 bg-orange-500/10';
      default: return 'border-blue-500/50 bg-blue-500/10';
    }
  };

  const getMaintenanceStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'border-blue-500/50 bg-blue-500/10';
      case 'in_progress': return 'border-orange-500/50 bg-orange-500/10';
      case 'completed': return 'border-green-500/50 bg-green-500/10';
      case 'cancelled': return 'border-gray-500/50 bg-gray-500/10';
      default: return 'border-gray-500/50 bg-gray-500/10';
    }
  };

  const renderPagination = (page, setPage, totalItems) => {
    if (totalItems <= ITEMS_PER_PAGE) return null;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    return (
      <div className="flex items-center justify-center space-x-4 mt-6">
        <button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.previous')}
        </button>
        <span className="text-gray-400 text-sm">
          {t('common.pageOf', { page: page + 1, total: totalPages })}
        </span>
        <button
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page >= totalPages - 1
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  const paginatedMaintenance = (maintenanceAlerts || []).slice(
    maintenancePage * ITEMS_PER_PAGE,
    (maintenancePage + 1) * ITEMS_PER_PAGE
  );
  const paginatedAnnouncements = (announcements || []).slice(
    announcementsPage * ITEMS_PER_PAGE,
    (announcementsPage + 1) * ITEMS_PER_PAGE
  );
  const paginatedNotifications = (notifications || []).slice(
    notificationsPage * ITEMS_PER_PAGE,
    (notificationsPage + 1) * ITEMS_PER_PAGE
  );

  return (
    <div className="space-y-6">
      {/* Maintenance Alerts Section */}
      <div className="football-card glass-football p-6 profile-notifications-no-clip">
        <h3 className="text-xl font-bold text-[#1e293b] mb-4 flex items-center">
          <Wrench className="w-5 h-5 mr-2 text-orange-500" />
          {t('notificationsTab.maintenanceTitle')}
        </h3>
        {loadingCommunications ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
          </div>
        ) : maintenanceAlerts && maintenanceAlerts.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedMaintenance.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border-2 ${getMaintenanceStatusColor(alert.status)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-white">{alert.title}</h4>
                  <span className={`px-2 py-1 rounded text-xs text-white ${
                    alert.status === 'scheduled' ? 'bg-blue-500' :
                    alert.status === 'in_progress' ? 'bg-orange-500' :
                    alert.status === 'completed' ? 'bg-green-500' : 'bg-gray-500'
                  }`}>
                    {alert.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-gray-300 text-sm mb-2">{alert.message}</p>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>{t('notificationsTab.scheduled')}: {formatDateTime(alert.scheduledStart, {}, locale)} - {formatDateTime(alert.scheduledEnd, {}, locale)}</p>
                  <p>{t('notificationsTab.affects')}: {alert.affects}</p>
                </div>
              </div>
              ))}
            </div>
            {renderPagination(maintenancePage, setMaintenancePage, maintenanceAlerts.length)}
          </>
        ) : (
          <p className="text-gray-400 text-center py-4">{t('notificationsTab.noMaintenance')}</p>
        )}
      </div>

      {/* Announcements Section */}
      <div className="football-card glass-football p-6 profile-notifications-no-clip">
        <h3 className="text-xl font-bold text-[#1e293b] mb-4 flex items-center">
          <Info className="w-5 h-5 mr-2 text-blue-500" />
          {t('notificationsTab.announcementsTitle')}
        </h3>
        {loadingCommunications ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
          </div>
        ) : announcements && announcements.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className={`p-4 rounded-lg border-2 ${getAnnouncementBgColor(announcement.type, announcement.priority)}`}
              >
                <div className="flex items-start space-x-3">
                  <div className="mt-0.5">
                    {getAnnouncementIcon(announcement.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-semibold text-white">{announcement.title}</h4>
                      {announcement.priority === 'critical' && (
                        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded">CRITICAL</span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-2">{announcement.message}</p>
                    <div className="text-xs text-gray-400">
                      {announcement.startDate && announcement.endDate && (
                        <p>Active: {formatDate(announcement.startDate, {}, locale)} - {formatDate(announcement.endDate, {}, locale)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
            {renderPagination(announcementsPage, setAnnouncementsPage, announcements.length)}
          </>
        ) : (
          <p className="text-gray-400 text-center py-4">{t('notificationsTab.noAnnouncements')}</p>
        )}
      </div>

      {/* Notifications Section — extra inner padding avoids clip-path cutting the header actions */}
      <div className="football-card glass-football p-6 profile-notifications-no-clip profile-notifications-personal-card">
        <div className="profile-notifications-header flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <h2 className="text-2xl font-bold text-[#1e293b] flex flex-wrap items-center gap-2 min-w-0 flex-1">
            <Bell className="w-6 h-6 flex-shrink-0" />
            <span className="break-words">{t('notificationsTab.personalTitle')}</span>
            {unreadCount > 0 && (
              <span className="px-3 py-1 bg-blue-500 text-white text-sm rounded-full whitespace-nowrap">
                {t('notificationsTab.unread', { count: unreadCount })}
              </span>
            )}
          </h2>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllAsRead}
              className="profile-notifications-mark-all text-sm font-semibold text-blue-400 hover:text-blue-300 whitespace-normal text-left sm:text-right sm:max-w-[min(100%,12rem)] sm:ml-3 flex-shrink-0"
            >
              {t('notificationsTab.markAllRead')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400">{t('notificationsTab.noPersonal')}</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  notification.isRead
                    ? 'border-gray-500/30 bg-gray-500/5'
                    : 'border-blue-500/50 bg-blue-500/10'
                }`}
                onClick={() => {
                  if (!notification.isRead) {
                    onMarkAsRead(notification.id);
                  }
                  const action = normalizeNotificationActionUrl(notification.actionUrl);
                  if (action?.type === 'internal') {
                    navigate(action.path, action.state ? { state: action.state } : {});
                  } else if (action?.type === 'external') {
                    window.location.href = action.url;
                  }
                }}
              >
                <div className="flex items-start space-x-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold platform-notification-text">{notification.title}</h3>
                      {!notification.isRead && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-1 ml-2"></div>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-2">{notification.message}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">{formatDate(notification.createdAt)}</p>
                      {notification.actionUrl && (
                        <span className="text-xs text-blue-400">{t('notificationsTab.clickToView')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
            {renderPagination(notificationsPage, setNotificationsPage, notifications.length)}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsTab;
